import { useCallback, useRef, useState } from 'react'

/**
 * Where a search box is, as opposed to what it found.
 *
 * `results` alone cannot say: an empty list means "nothing matched", "nothing
 * asked yet" and "still looking" all at once, and a box that cannot tell them
 * apart ends up asserting the first while one of the others is true. That is the
 * bug behind a dropdown reading "No results" for the whole time it is waiting.
 */
export type AsyncSearchStatus = 'idle' | 'searching' | 'ready' | 'failed'

/**
 * One async search box's results, with the two guarantees every one of them
 * needs and most of ours were missing.
 *
 * **Only the newest request paints.** Responses arrive out of order — a short
 * query matches far more rows than the longer one typed after it and can take
 * longer to come back — so each run carries an id and a superseded reply is
 * dropped rather than allowed to overwrite a fresher list.
 *
 * **A failure keeps the last good list.** Clearing on error is indistinguishable
 * from "no such thing exists", which is a worse lie than showing a slightly stale
 * list; `status` is what tells the caller to say so.
 */
const useAsyncSearch = <T>() => {
  const [results, setResults] = useState<T[]>([])
  const [status, setStatus] = useState<AsyncSearchStatus>('idle')

  const requestIdRef = useRef(0)

  /** Invalidate anything in flight, so a reply owed to an older query is dropped. */
  const supersede = useCallback(() => {
    requestIdRef.current += 1
  }, [])

  /**
   * Paint a provisional list without claiming the search is over.
   *
   * Deliberately leaves `status` alone. A local list is something to look at
   * while the request runs, not an answer — and a caller that shows one has
   * usually just marked a search as started, so settling it here would report
   * the wait as finished before it began.
   */
  const preview = useCallback(
    (next: T[]) => {
      supersede()
      setResults(next)
    },
    [supersede],
  )

  /**
   * Mark a search as started before the request actually goes out.
   *
   * The wait a reader perceives begins at the keystroke, not when a debounce
   * elapses — so a box that only reports "searching" once the request is in
   * flight spends its whole debounce window claiming it has an answer.
   */
  const markSearching = useCallback(() => setStatus('searching'), [])

  /** End the wait without disturbing the results — for a box nobody is reading any more. */
  const markSettled = useCallback(() => setStatus('ready'), [])

  /** Back to having asked nothing. */
  const reset = useCallback(() => {
    supersede()
    setResults([])
    setStatus('idle')
  }, [supersede])

  const run = useCallback(async (load: () => Promise<T[]>) => {
    const requestId = (requestIdRef.current += 1)
    setStatus('searching')
    try {
      const next = await load()
      if (requestId !== requestIdRef.current) return
      setResults(next)
      setStatus('ready')
    } catch {
      if (requestId !== requestIdRef.current) return
      // `results` deliberately untouched — see the failure guarantee above.
      setStatus('failed')
    }
  }, [])

  return { results, status, run, preview, markSearching, markSettled, reset, supersede }
}

export default useAsyncSearch
